/**
 * @Module: Sample Recipe Data
 * @Status: Ready
 * @Summary: Three plant-based starter recipes for new users
 * @Description: Snickerdoodle cookies (flax egg), sweet potato hash with
 *   maple-candied pecans, and quick pizza from scratch. All plant-based,
 *   basic in structure, approachable for beginners.
 */

import { Recipe } from '../types/recipe';

/** Look up a sample recipe by ID. */
export function getSampleRecipe(id: string): Recipe | undefined {
  return sampleRecipes.find(r => r.id === id);
}

export const sampleRecipes: Recipe[] = [
  // --- 1. Snickerdoodle Cookies (Flax Egg) ---
  {
    id: 'snickerdoodle-cookies',
    title: 'Snickerdoodle Cookies',
    description: 'Soft, chewy cinnamon-sugar cookies with a flax egg — fully plant-based',
    source: { type: 'manual' },
    meta: {
      servings: 24,
      prepMinutes: 15,
      cookMinutes: 12,
      totalMinutes: 27,
      cuisine: 'American',
      difficulty: 'easy',
      tags: ['dessert', 'vegan', 'cookies', 'baking'],
      unitSystem: 'imperial',
    },
    steps: [
      {
        number: 1,
        title: 'Make the flax egg',
        instruction: 'In a small bowl, mix ground flaxseed with water. Stir well and set aside for 5 minutes until it thickens into a gel — this replaces a regular egg.',
        ingredients: [
          { name: 'ground flaxseed', amount: 1, unit: 'tbsp' },
          { name: 'water', amount: 3, unit: 'tbsp', notes: 'room temperature' },
        ],
        durationMinutes: 5,
        technique: 'mix',
        tip: 'The flax egg should be thick and gel-like before using. If it looks watery, give it another minute.',
      },
      {
        number: 2,
        title: 'Cream the butter and sugar',
        instruction: 'In a large bowl, cream together the softened vegan butter and sugar until light and fluffy, about 2 minutes. Add the flax egg and vanilla extract, mix until combined.',
        ingredients: [
          { name: 'vegan butter', amount: 0.5, unit: 'cup', notes: 'softened to room temperature' },
          { name: 'granulated sugar', amount: 0.75, unit: 'cup' },
          { name: 'vanilla extract', amount: 1, unit: 'tsp' },
        ],
        durationMinutes: 3,
        technique: 'mix',
      },
      {
        number: 3,
        title: 'Mix the dry ingredients',
        instruction: 'In a separate bowl, whisk together the flour, cream of tartar, baking soda, and salt.',
        ingredients: [
          { name: 'all-purpose flour', amount: 1.5, unit: 'cups' },
          { name: 'cream of tartar', amount: 1, unit: 'tsp', notes: 'this gives snickerdoodles their signature tang' },
          { name: 'baking soda', amount: 0.5, unit: 'tsp' },
          { name: 'salt', amount: 0.25, unit: 'tsp' },
        ],
        durationMinutes: 2,
        technique: 'mix',
        tip: 'Cream of tartar is the secret to snickerdoodles. Don\'t skip it — it gives that distinctive tangy chewiness.',
      },
      {
        number: 4,
        title: 'Combine and chill',
        instruction: 'Gradually add the dry ingredients to the wet mixture, stirring until just combined. Don\'t overmix. Chill the dough in the fridge for 15 minutes — this makes them easier to roll.',
        ingredients: [],
        durationMinutes: 15,
        technique: 'chill',
      },
      {
        number: 5,
        title: 'Roll in cinnamon sugar',
        instruction: 'Preheat oven to 375°F (190°C). Mix the cinnamon and sugar in a small bowl. Roll tablespoon-sized balls of dough in the cinnamon-sugar mixture. Place on a lined baking sheet 2 inches apart.',
        ingredients: [
          { name: 'ground cinnamon', amount: 2, unit: 'tsp' },
          { name: 'granulated sugar', amount: 2, unit: 'tbsp', notes: 'for rolling' },
        ],
        durationMinutes: 5,
        technique: 'bake',
      },
      {
        number: 6,
        title: 'Bake',
        instruction: 'Bake for 10-12 minutes until edges are set but centers still look slightly underdone. They\'ll firm up as they cool. Let cool on the baking sheet for 5 minutes before transferring to a wire rack.',
        ingredients: [],
        durationMinutes: 12,
        technique: 'bake',
        tip: 'Pull them when they still look a tiny bit underdone in the center — they continue cooking on the hot sheet. Overbaking = crunchy instead of chewy.',
      },
    ],
    notes: 'Makes about 24 cookies. Store in an airtight container for up to 5 days. The flax egg works perfectly here — you won\'t miss the regular egg.',
  },

  // --- 2. Sweet Potato Hash with Maple-Candied Pecans ---
  {
    id: 'sweet-potato-hash',
    title: 'Sweet Potato Hash with Maple-Candied Pecans',
    description: 'Crispy sweet potato hash topped with a warm maple glaze and candied pecans',
    source: { type: 'manual' },
    meta: {
      servings: 2,
      prepMinutes: 10,
      cookMinutes: 25,
      totalMinutes: 35,
      cuisine: 'American',
      difficulty: 'easy',
      tags: ['breakfast', 'vegan', 'sweet potato', 'comfort food'],
      unitSystem: 'imperial',
    },
    steps: [
      {
        number: 1,
        title: 'Prep the sweet potatoes',
        instruction: 'Peel and dice the sweet potatoes into small ½-inch cubes. Smaller = crispier. Dice the onion and mince the garlic.',
        ingredients: [
          { name: 'sweet potatoes', amount: 2, unit: 'medium', notes: 'about 1 lb total' },
          { name: 'yellow onion', amount: 0.5, unit: 'medium', notes: 'diced' },
          { name: 'garlic', amount: 2, unit: 'cloves', notes: 'minced' },
        ],
        durationMinutes: 5,
        technique: 'chop',
      },
      {
        number: 2,
        title: 'Crisp the sweet potatoes',
        instruction: 'Heat olive oil in a large skillet over medium-high heat. Add the sweet potatoes in a single layer — don\'t crowd them. Cook without stirring for 4-5 minutes until golden on the bottom. Flip and cook another 4-5 minutes. Season with salt and smoked paprika.',
        ingredients: [
          { name: 'olive oil', amount: 2, unit: 'tbsp' },
          { name: 'salt', amount: 0.5, unit: 'tsp' },
          { name: 'smoked paprika', amount: 0.5, unit: 'tsp' },
        ],
        durationMinutes: 10,
        technique: 'saute',
        tip: 'The key to crispy hash is patience — let the potatoes sit undisturbed so they develop a golden crust.',
      },
      {
        number: 3,
        title: 'Add the aromatics',
        instruction: 'Push the potatoes to one side. Add the diced onion and cook for 2-3 minutes until softened. Add the garlic and cook for 30 seconds until fragrant. Toss everything together.',
        ingredients: [],
        durationMinutes: 3,
        technique: 'saute',
      },
      {
        number: 4,
        title: 'Candy the pecans',
        instruction: 'In a small pan over medium heat, add the pecans, maple syrup, and a pinch of salt. Stir constantly for 2-3 minutes until the syrup thickens and coats the pecans. Remove from heat immediately — they burn fast.',
        ingredients: [
          { name: 'pecan halves', amount: 0.33, unit: 'cup' },
          { name: 'maple syrup', amount: 2, unit: 'tbsp' },
          { name: 'salt', amount: 1, unit: 'pinch', notes: 'for the pecans' },
        ],
        durationMinutes: 3,
        technique: 'saute',
        tip: 'Watch the pecans like a hawk — they go from perfectly candied to burnt in about 10 seconds.',
      },
      {
        number: 5,
        title: 'Glaze and serve',
        instruction: 'Drizzle the remaining maple syrup over the hash. Top with the candied pecans and a sprinkle of flaky salt if you have it. Serve hot.',
        ingredients: [
          { name: 'maple syrup', amount: 1, unit: 'tbsp', notes: 'for drizzling' },
        ],
        durationMinutes: 1,
        technique: 'toss',
      },
    ],
    notes: 'This is breakfast comfort food at its best. The sweet-salty-crispy combination is addictive. Pairs well with a side of toast or fresh fruit.',
  },

  // --- 3. Quick Pizza from Scratch ---
  {
    id: 'quick-pizza',
    title: 'Quick Pizza from Scratch',
    description: 'Homemade pizza with a fast instant-yeast dough — no waiting, no fuss',
    source: { type: 'manual' },
    meta: {
      servings: 2,
      prepMinutes: 15,
      cookMinutes: 12,
      totalMinutes: 27,
      cuisine: 'Italian',
      difficulty: 'easy',
      tags: ['dinner', 'vegan', 'pizza', 'quick'],
      unitSystem: 'imperial',
    },
    steps: [
      {
        number: 1,
        title: 'Activate the yeast',
        instruction: 'In a large bowl, combine the warm water (it should feel like a warm bath — about 110°F/43°C), sugar, and instant yeast. Stir gently and let it sit for 5 minutes until it gets foamy on top.',
        ingredients: [
          { name: 'warm water', amount: 0.75, unit: 'cup', notes: '110°F / 43°C — warm but not hot' },
          { name: 'granulated sugar', amount: 1, unit: 'tsp' },
          { name: 'instant yeast', amount: 1, unit: 'packet', notes: '2¼ tsp / 7g' },
        ],
        durationMinutes: 5,
        technique: 'mix',
        tip: 'If the water is too hot it kills the yeast, too cold and it won\'t activate. Test on your wrist — it should feel comfortably warm, not hot.',
      },
      {
        number: 2,
        title: 'Make the dough',
        instruction: 'Add the olive oil and salt to the yeast mixture. Gradually add the flour, stirring with a wooden spoon until a shaggy dough forms. Turn out onto a floured surface and knead for 3-4 minutes until smooth and elastic.',
        ingredients: [
          { name: 'olive oil', amount: 2, unit: 'tbsp', notes: 'plus extra for the bowl' },
          { name: 'salt', amount: 0.75, unit: 'tsp' },
          { name: 'all-purpose flour', amount: 2, unit: 'cups', notes: 'plus extra for kneading' },
        ],
        durationMinutes: 5,
        technique: 'mix',
        tip: 'The dough should be slightly tacky but not sticky. If it clings to your hands, add flour a tablespoon at a time.',
      },
      {
        number: 3,
        title: 'Shape the pizza',
        instruction: 'Preheat oven to 475°F (245°C) — as hot as it goes. Oil a baking sheet or pizza stone. Press and stretch the dough into a rough circle or rectangle on the sheet. Don\'t worry about perfection — rustic is the vibe.',
        ingredients: [],
        durationMinutes: 3,
        technique: 'bake',
      },
      {
        number: 4,
        title: 'Top it',
        instruction: 'Spread the pizza sauce evenly over the dough, leaving a ½-inch border for the crust. Tear the vegan mozzarella (or sprinkle shreds) over the sauce. Add a generous sprinkle of dried oregano and any other toppings you like.',
        ingredients: [
          { name: 'pizza sauce', amount: 0.33, unit: 'cup', notes: 'or marinara' },
          { name: 'vegan mozzarella', amount: 1, unit: 'cup', notes: 'shredded or torn — or use regular mozzarella' },
          { name: 'dried oregano', amount: 1, unit: 'tsp' },
        ],
        durationMinutes: 2,
        technique: 'toss',
        tip: 'Optional toppings: sliced mushrooms, olives, roasted red peppers, fresh basil (add after baking), red pepper flakes.',
      },
      {
        number: 5,
        title: 'Bake',
        instruction: 'Bake for 10-12 minutes until the crust is golden and the cheese is bubbly with brown spots. Let cool for 2 minutes before slicing. Drizzle with a little olive oil if you\'re feeling fancy.',
        ingredients: [],
        durationMinutes: 12,
        technique: 'bake',
        tip: 'The hotter your oven, the better the crust. If you have a pizza stone, preheat it for 30 minutes for restaurant-quality results.',
      },
    ],
    notes: 'This dough comes together in under 10 minutes with no rising time needed. The instant yeast does the work in the oven. Leftover dough can be wrapped and refrigerated for up to 2 days.',
  },
];
